import React from "react";
import { api } from "../lib/api";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "./ui/tabs";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { TrendingUp, ChevronRight, Filter } from "lucide-react";
import CreatorChannelMatrix from "./CreatorChannelMatrix";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function BrandPerformance() {
    const [data, setData] = React.useState(null);
    const [creators, setCreators] = React.useState([]);
    const [filters, setFilters] = React.useState({ creator_id: "all", channel: "all" });
    const [openVideo, setOpenVideo] = React.useState(null);

    const load = React.useCallback(() => {
        const params = {};
        if (filters.creator_id !== "all") params.creator_id = filters.creator_id;
        if (filters.channel !== "all") params.channel = filters.channel;
        api.get("/brand/performance", { params }).then((r) => setData(r.data));
    }, [filters]);

    React.useEffect(() => {
        api.get("/brand/creators").then((r) => setCreators(r.data));
    }, []);

    React.useEffect(() => {
        load();
    }, [load]);

    if (!data) return <div className="text-slate-500 overline">Loading…</div>;

    const channelOptions = ["all", ...(data.by_channel || []).map((c) => c.channel)];

    return (
        <div className="space-y-6" data-testid="brand-performance">
            {/* Top totals */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                {[
                    ["Total spend", fmt(data.total_spend)],
                    ["Revenue", fmt(data.total_revenue), "coral"],
                    ["Creators paid", fmt(data.total_commission)],
                    ["Brand kept", fmt(data.brand_kept)],
                    ["ROAS", `${data.roas || 0}x`, "lime"],
                ].map(([l, v, accent]) => (
                    <div key={l} className="card-ecom p-4">
                        <div className="text-xs text-slate-500">{l}</div>
                        <div
                            className={`font-display text-2xl font-semibold mt-1 ${
                                accent === "coral" ? "text-coral" : accent === "lime" ? "text-lime-ink" : ""
                            }`}
                        >
                            {v}
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter bar */}
            <div className="card-ecom p-4 flex flex-wrap items-center gap-3" data-testid="performance-filters">
                <div className="flex items-center gap-2 text-slate-600">
                    <Filter className="w-4 h-4" />
                    <span className="overline">FILTERS</span>
                </div>
                <Select
                    value={filters.creator_id}
                    onValueChange={(v) => setFilters({ ...filters, creator_id: v })}
                >
                    <SelectTrigger className="rounded-full border-slate-200 h-10 w-56" data-testid="filter-creator">
                        <SelectValue placeholder="All creators" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                        <SelectItem value="all">All creators</SelectItem>
                        {creators.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                                {c.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select
                    value={filters.channel}
                    onValueChange={(v) => setFilters({ ...filters, channel: v })}
                >
                    <SelectTrigger className="rounded-full border-slate-200 h-10 w-44" data-testid="filter-channel">
                        <SelectValue placeholder="All channels" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                        {channelOptions.map((c) => (
                            <SelectItem key={c} value={c}>
                                {c === "all" ? "All channels" : c}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <span className="chip chip-lime ml-auto">
                    <TrendingUp className="w-3 h-3" /> MOCKED · MVP
                </span>
            </div>

            <Tabs defaultValue="attribution">
                <TabsList className="rounded-full bg-slate-100 p-1 h-auto" data-testid="performance-subtabs">
                    {[
                        ["attribution", "Attribution"],
                        ["campaigns", "Campaigns"],
                        ["videos", "Videos"],
                        ["creators", "Creators"],
                        ["channels", "Channels"],
                    ].map(([v, l]) => (
                        <TabsTrigger
                            key={v}
                            value={v}
                            className="rounded-full data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm px-5 py-2"
                            data-testid={`perf-tab-${v}`}
                        >
                            {l}
                        </TabsTrigger>
                    ))}
                </TabsList>

                <TabsContent value="attribution" className="mt-5">
                    <CreatorChannelMatrix data={data} />
                </TabsContent>

                <TabsContent value="campaigns" className="mt-5">
                    {data.rows.length === 0 ? (
                        <Empty />
                    ) : (
                        <div className="card-ecom overflow-hidden" data-testid="perf-campaigns">
                            <Header cols={["Campaign", "Channels", "Spend", "Revenue", "Creator paid"]} />
                            {data.rows.map((r) => (
                                <Row key={r.campaign_id} testid={`row-camp-${r.campaign_id}`}>
                                    <Cell span={5}>
                                        <div className="font-display font-semibold text-slate-900 truncate">
                                            {r.name}
                                        </div>
                                        <div className="text-xs text-slate-500">
                                            {r.n_videos} videos · {r.rev_share_pct ? `${r.rev_share_pct}% rev-share` : "flat"}
                                        </div>
                                    </Cell>
                                    <Cell span={3}>
                                        <ChannelChips channels={r.channels} />
                                    </Cell>
                                    <Cell span={1} right>{fmt(r.spend)}</Cell>
                                    <Cell span={1} right strong>{fmt(r.revenue)}</Cell>
                                    <Cell span={2} right strong color="coral">{fmt(r.creator_paid)}</Cell>
                                </Row>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="videos" className="mt-5">
                    {data.by_video.length === 0 ? (
                        <Empty />
                    ) : (
                        <div className="card-ecom overflow-hidden" data-testid="perf-videos">
                            <Header cols={["Video", "Creator · Channels", "Spend", "Revenue", "Creator paid"]} />
                            {data.by_video.map((v) => (
                                <React.Fragment key={v.video_id}>
                                    <Row
                                        testid={`row-video-${v.video_id}`}
                                        onClick={() => setOpenVideo(openVideo === v.video_id ? null : v.video_id)}
                                        clickable
                                    >
                                        <Cell span={5}>
                                            <div className="flex items-center gap-2">
                                                <ChevronRight
                                                    className={`w-4 h-4 text-slate-400 transition-transform ${openVideo === v.video_id ? "rotate-90" : ""}`}
                                                />
                                                <div>
                                                    <div className="font-display font-semibold text-slate-900 truncate">
                                                        {v.title}
                                                    </div>
                                                    <div className="text-xs text-slate-500">
                                                        {v.campaigns} campaigns
                                                    </div>
                                                </div>
                                            </div>
                                        </Cell>
                                        <Cell span={3}>
                                            <div className="text-sm text-slate-700 truncate">{v.creator_name}</div>
                                            <ChannelChips channels={v.channels} />
                                        </Cell>
                                        <Cell span={1} right>{fmt(v.spend)}</Cell>
                                        <Cell span={1} right strong>{fmt(v.revenue)}</Cell>
                                        <Cell span={2} right strong color="coral">{fmt(v.creator_paid)}</Cell>
                                    </Row>
                                    {openVideo === v.video_id && (
                                        <div className="bg-slate-50 px-5 py-4 text-sm text-slate-600 border-b hairline">
                                            <div className="overline mb-2">CHANNELS RUNNING THIS VIDEO</div>
                                            <div className="flex flex-wrap gap-2">
                                                {v.channels.map((ch) => (
                                                    <span key={ch} className="chip chip-lime">{ch}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="creators" className="mt-5">
                    {data.by_creator.length === 0 ? (
                        <Empty />
                    ) : (
                        <div className="card-ecom overflow-hidden" data-testid="perf-creators">
                            <Header cols={["Creator", "Videos · Campaigns", "Spend", "Revenue", "Paid out"]} />
                            {data.by_creator.map((c) => (
                                <Row key={c.creator_id} testid={`row-creator-${c.creator_id}`}>
                                    <Cell span={5}>
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-coral to-lime flex items-center justify-center text-white font-display font-bold">
                                                {c.creator_name?.[0]?.toUpperCase()}
                                            </div>
                                            <div className="font-display font-semibold text-slate-900 truncate">
                                                {c.creator_name}
                                            </div>
                                        </div>
                                    </Cell>
                                    <Cell span={3}>
                                        <div className="text-xs text-slate-500">
                                            {c.videos} videos · {c.campaigns} campaigns
                                        </div>
                                    </Cell>
                                    <Cell span={1} right>{fmt(c.spend)}</Cell>
                                    <Cell span={1} right strong>{fmt(c.revenue)}</Cell>
                                    <Cell span={2} right strong color="coral">{fmt(c.creator_paid)}</Cell>
                                </Row>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="channels" className="mt-5">
                    {data.by_channel.length === 0 ? (
                        <Empty />
                    ) : (
                        <div className="card-ecom overflow-hidden" data-testid="perf-channels">
                            <Header cols={["Channel", "", "Spend", "Revenue", "Creator paid"]} />
                            {data.by_channel.map((c) => (
                                <Row key={c.channel} testid={`row-ch-${c.channel}`}>
                                    <Cell span={5}>
                                        <span className="chip chip-lime">{c.channel}</span>
                                    </Cell>
                                    <Cell span={3} />
                                    <Cell span={1} right>{fmt(c.spend)}</Cell>
                                    <Cell span={1} right strong>{fmt(c.revenue)}</Cell>
                                    <Cell span={2} right strong color="coral">{fmt(c.creator_paid)}</Cell>
                                </Row>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}

const Header = ({ cols }) => (
    <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b hairline text-xs uppercase tracking-wider text-slate-500 font-semibold bg-slate-50">
        <div className="col-span-5">{cols[0]}</div>
        <div className="col-span-3">{cols[1]}</div>
        <div className="col-span-1 text-right">{cols[2]}</div>
        <div className="col-span-1 text-right">{cols[3]}</div>
        <div className="col-span-2 text-right">{cols[4]}</div>
    </div>
);

const Row = ({ children, testid, onClick, clickable }) => (
    <div
        data-testid={testid}
        onClick={onClick}
        className={`grid grid-cols-12 gap-3 px-5 py-4 border-b hairline last:border-0 items-center ${clickable ? "cursor-pointer hover:bg-slate-50" : ""}`}
    >
        {children}
    </div>
);

const Cell = ({ span, right, strong, color, children }) => (
    <div
        className={`col-span-${span} ${right ? "text-right" : ""} ${
            strong ? "font-display text-base font-semibold" : "text-sm"
        } ${color === "coral" ? "text-coral" : "text-slate-700"}`}
    >
        {children}
    </div>
);

const ChannelChips = ({ channels }) => (
    <div className="flex flex-wrap gap-1 mt-1">
        {(channels || []).slice(0, 3).map((ch) => (
            <span key={ch} className="chip chip-lime">{ch}</span>
        ))}
        {channels && channels.length > 3 && (
            <span className="chip">+{channels.length - 3}</span>
        )}
    </div>
);

const Empty = () => (
    <div className="card-ecom p-12 text-center text-slate-500" data-testid="perf-empty">
        Push a campaign to start tracking performance.
    </div>
);
